// =============================================================================
// ACTION GENERATION SERVICE
// =============================================================================
// Comprehensive action generation using ALL available data sources
// Generates actions for ALL relevant domains (not just milestone domain)
// =============================================================================

import {
  Goal,
  Milestone,
  ActionPriority,
  ActionType,
  detectActionTypeFromTitle,
} from './types';
import {
  CapabilityDomain,
  CAPABILITY_DOMAIN_LABELS,
  CompleteAssessment,
  ExtendedCapabilityDecision,
  CapabilityDecision,
} from './capabilities/types';
import { SparringMessage } from './types';
import { LearningStyle } from './assessment/testDefinitions';
import { generateLearningAction } from './utils/learningStyleActions';

// =============================================================================
// TYPES
// =============================================================================

export interface GeneratedAction {
  title: string;
  priority: ActionPriority;
  actionType: ActionType;
  domain: CapabilityDomain;
  domainLabel: string;
  source: string; // Where this action came from (for debugging)
}

interface ActionGenerationContext {
  goal: Goal;
  milestone: Milestone;
  completeAssessment?: CompleteAssessment;
  extendedDecisions?: ExtendedCapabilityDecision[];
  sparringMessages?: SparringMessage[];
  userResponses?: string[];
  selfTestResults?: {
    rolePreference?: 'doer' | 'thinker' | 'connector' | 'leader';
    learningStyle?: LearningStyle;
    knowledgeLevels?: Record<string, string>;
  };
  preSparringAnswers?: Record<string, string>;
  entrepreneurContext?: any;
  // ✅ FIX: Allow filtering to specific domain only
  filterToDomain?: CapabilityDomain; // If provided, only generate actions for this domain
}

// =============================================================================
// CONSTANTS
// =============================================================================

const ALL_DOMAINS: CapabilityDomain[] = [
  'strategy',
  'marketing',
  'sales',
  'product',
  'operations',
  'finance',
  'team',
];

// Domain keywords for text matching
const DOMAIN_KEYWORDS: Record<CapabilityDomain, string[]> = {
  strategy: ['strategie', 'richting', 'visie', 'plan', 'doel', 'focus', 'missie', 'doelstelling'],
  marketing: ['marketing', 'promotie', 'advertentie', 'campagne', 'content', 'social media', 'seo', 'zichtbaarheid', 'instagram', 'facebook', 'linkedin'],
  sales: ['verkoop', 'sales', 'klant', 'lead', 'conversie', 'omzet', 'verkopen', 'klanten', 'leads'],
  product: ['product', 'dienst', 'ontwikkeling', 'feature', 'functionaliteit', 'aanbod', 'service'],
  operations: ['operatie', 'proces', 'systeem', 'workflow', 'automatisering', 'efficiëntie', 'organisatie'],
  finance: ['financiën', 'budget', 'geld', 'kosten', 'omzet', 'winst', 'investering', '€', 'euro'],
  team: ['team', 'medewerker', 'collega', 'aannemen', 'werven', 'delegeren', 'capaciteit', 'personeel'],
};

// =============================================================================
// MAIN GENERATION FUNCTION
// =============================================================================

/**
 * Generate comprehensive actions for a milestone
 * Uses ALL available data sources and generates actions for relevant domains
 * ✅ FIX: Can now be filtered to a single domain to avoid duplicate actions across milestones
 */
export async function generateComprehensiveActions(
  context: ActionGenerationContext
): Promise<GeneratedAction[]> {
  const allActions: GeneratedAction[] = [];
  const seenTitles = new Set<string>();

  // ✅ FIX: If filterToDomain is provided, only generate actions for that domain
  let relevantDomains: CapabilityDomain[];
  if (context.filterToDomain) {
    relevantDomains = [context.filterToDomain];
    console.log(`🎯 Generating actions for SPECIFIC domain only: ${context.filterToDomain}`);
  } else {
    // Get ALL relevant domains (original behavior)
    relevantDomains = getRelevantDomains(context);
    console.log(`🌍 Generating actions for ALL ${relevantDomains.length} relevant domains`);
  }

  // Generate actions PER DOMAIN
  for (const domain of relevantDomains) {
    const domainActions = await generateActionsForDomain(
      domain,
      context,
      seenTitles,
      context.selfTestResults?.learningStyle
    );

    allActions.push(...domainActions);
  }

  // Distribute over timeline
  return distributeActionsOverTimeline(
    allActions,
    new Date(context.goal.startDate),
    new Date(context.goal.endDate)
  );
}

// =============================================================================
// DOMAIN DETECTION
// =============================================================================

/**
 * Get ALL relevant domains from assessment and context
 */
function getRelevantDomains(context: ActionGenerationContext): CapabilityDomain[] {
  const domains = new Set<CapabilityDomain>();

  // 1. Add ALL domains from assessment decisions
  if (context.completeAssessment?.decisions) {
    for (const decision of context.completeAssessment.decisions) {
      if (decision.required) {
        domains.add(decision.domain);
      }
    }
  }

  // 2. Add ALL domains with extended decisions
  if (context.extendedDecisions) {
    for (const extDecision of context.extendedDecisions) {
      domains.add(extDecision.domain);
    }
  }

  // 3. Add domains mentioned in goal description
  if (context.goal.description) {
    const goalDomains = extractDomainsFromText(context.goal.description);
    goalDomains.forEach(d => domains.add(d));
  }

  // 4. Add domains mentioned in sparring responses
  if (context.userResponses) {
    for (const response of context.userResponses) {
      const responseDomains = extractDomainsFromText(response);
      responseDomains.forEach(d => domains.add(d));
    }
  }

  // 5. If no domains found, use all 7 domains as fallback
  if (domains.size === 0) {
    return ALL_DOMAINS;
  }

  return Array.from(domains);
}

/**
 * Extract domains from text using keyword matching
 */
function extractDomainsFromText(text: string): CapabilityDomain[] {
  const domains: CapabilityDomain[] = [];
  const lowerText = text.toLowerCase();

  for (const [domain, keywords] of Object.entries(DOMAIN_KEYWORDS)) {
    if (keywords.some(kw => lowerText.includes(kw))) {
      domains.push(domain as CapabilityDomain);
    }
  }

  return domains;
}

// =============================================================================
// DOMAIN-SPECIFIC ACTION GENERATION
// =============================================================================

/**
 * Generate actions for ONE specific domain
 * Uses ALL available data for that domain
 */
async function generateActionsForDomain(
  domain: CapabilityDomain,
  context: ActionGenerationContext,
  seenTitles: Set<string>,
  learningStyle?: LearningStyle
): Promise<GeneratedAction[]> {
  const domainActions: GeneratedAction[] = [];
  const domainLabel = CAPABILITY_DOMAIN_LABELS[domain];

  // ============================================================
  // BRON 1: Extended Capability Decision (voor dit domein)
  // ============================================================
  const extendedDecision = context.extendedDecisions?.find(d => d.domain === domain);

  if (extendedDecision) {
    // firstAction
    if (extendedDecision.firstAction) {
      const actions = extractActionsFromText(extendedDecision.firstAction);
      addUniqueActions(actions, domainActions, seenTitles, 'high', `extended_firstAction_${domain}`, domain, domainLabel);
    }

    // missingKnowledge[] → Multiple learning actions per item (adapted to learning style)
    if (extendedDecision.missingKnowledge && extendedDecision.missingKnowledge.length > 0) {
      for (const knowledge of extendedDecision.missingKnowledge) {
        const learningActions = generateLearningActions(knowledge, domainLabel, learningStyle);
        addUniqueActions(learningActions, domainActions, seenTitles, 'medium', `extended_missingKnowledge_${domain}`, domain, domainLabel);
      }
    }

    // biggestChallenge → Multiple challenge-tackling actions
    if (extendedDecision.biggestChallenge) {
      const challengeActions = generateChallengeActions(
        extendedDecision.biggestChallenge,
        domainLabel
      );
      addUniqueActions(challengeActions, domainActions, seenTitles, 'high', `extended_biggestChallenge_${domain}`, domain, domainLabel);
    }

    // currentActivities[] → Integration actions per activity
    if (extendedDecision.currentActivities && extendedDecision.currentActivities.length > 0) {
      for (const activity of extendedDecision.currentActivities) {
        const integrationActions = generateIntegrationActions(activity, domainLabel);
        addUniqueActions(integrationActions, domainActions, seenTitles, 'medium', `extended_currentActivities_${domain}`, domain, domainLabel);
      }
    }

    // toolsUsed → Setup actions per tool
    if (extendedDecision.toolsUsed) {
      const tools = extractToolsFromText(extendedDecision.toolsUsed);
      for (const tool of tools) {
        const toolActions = generateToolActions(tool, domainLabel);
        addUniqueActions(toolActions, domainActions, seenTitles, 'medium', `extended_toolsUsed_${domain}`, domain, domainLabel);
      }
    }

    // skillRatings → Development actions for low ratings
    if (extendedDecision.skillRatings) {
      for (const [skill, rating] of Object.entries(extendedDecision.skillRatings)) {
        if (rating < 3) {
          const skillActions = generateSkillDevelopmentActions(skill, domainLabel);
          addUniqueActions(skillActions, domainActions, seenTitles, 'medium', `extended_skillRatings_${domain}`, domain, domainLabel);
        }
      }
    }

    // timeInvestment → Planning actions
    if (extendedDecision.timeInvestment) {
      const timeActions = generateTimePlanningActions(
        extendedDecision.timeInvestment,
        domainLabel
      );
      addUniqueActions(timeActions, domainActions, seenTitles, 'medium', `extended_timeInvestment_${domain}`, domain, domainLabel);
    }

    // timeline → Timing actions
    if (extendedDecision.timeline) {
      const timelineActions = generateTimelineActions(
        extendedDecision.timeline,
        domainLabel
      );
      addUniqueActions(timelineActions, domainActions, seenTitles, 'high', `extended_timeline_${domain}`, domain, domainLabel);
    }

    // budget → Budget actions
    if (extendedDecision.budget) {
      const budgetActions = generateBudgetActions(
        extendedDecision.budget,
        domainLabel
      );
      addUniqueActions(budgetActions, domainActions, seenTitles, 'high', `extended_budget_${domain}`, domain, domainLabel);
    }

    // extendedAnswers → Extract actions from all answers
    if (extendedDecision.extendedAnswers) {
      for (const [questionId, answer] of Object.entries(extendedDecision.extendedAnswers)) {
        if (typeof answer === 'string' && answer.length > 10) {
          const answerActions = extractActionsFromText(answer);
          addUniqueActions(answerActions, domainActions, seenTitles, 'medium', `extended_answers_${domain}_${questionId}`, domain, domainLabel);
        } else if (Array.isArray(answer)) {
          for (const item of answer) {
            if (typeof item === 'string' && item.length > 10) {
              const itemActions = extractActionsFromText(item);
              addUniqueActions(itemActions, domainActions, seenTitles, 'medium', `extended_answers_${domain}_${questionId}`, domain, domainLabel);
            }
          }
        }
      }
    }
  }

  // ============================================================
  // BRON 2: Basic Capability Decision (voor dit domein)
  // ============================================================
  const basicDecision = context.completeAssessment?.decisions?.find(d => d.domain === domain);

  if (basicDecision && !extendedDecision) {
    // Use basic decision data
    if (basicDecision.resolution === 'learn') {
      const learnActions = generateLearningActionsForDomain(domainLabel);
      addUniqueActions(learnActions, domainActions, seenTitles, 'medium', `basic_decision_learn_${domain}`, domain, domainLabel);
    } else if (basicDecision.resolution === 'outsource') {
      const outsourceActions = generateOutsourceActions(domainLabel);
      addUniqueActions(outsourceActions, domainActions, seenTitles, 'high', `basic_decision_outsource_${domain}`, domain, domainLabel);
    } else if (basicDecision.resolution === 'hire') {
      const hireActions = generateHireActions(domainLabel);
      addUniqueActions(hireActions, domainActions, seenTitles, 'high', `basic_decision_hire_${domain}`, domain, domainLabel);
    }
  }

  // ============================================================
  // BRON 3: Goal Description (domain-specific extraction)
  // ============================================================
  if (context.goal.description) {
    const goalActions = extractDomainSpecificActionsFromGoal(
      context.goal.description,
      domain
    );
    addUniqueActions(goalActions, domainActions, seenTitles, 'high', `goal_description_${domain}`, domain, domainLabel);
  }

  // ============================================================
  // BRON 4: User Responses (domain-specific filtering)
  // ============================================================
  if (context.userResponses && context.userResponses.length > 0) {
    const domainKeywords = DOMAIN_KEYWORDS[domain];
    const relevantResponses = context.userResponses.filter(response => 
      domainKeywords.some(kw => response.toLowerCase().includes(kw.toLowerCase())) ||
      response.length > 100 // Long responses likely contain useful info
    );

    for (const response of relevantResponses) {
      const responseActions = extractDomainSpecificActionsFromResponse(response, domain);
      addUniqueActions(responseActions, domainActions, seenTitles, 'medium', `user_responses_${domain}`, domain, domainLabel);
    }
  }

  // ============================================================
  // BRON 5: Pre-Sparring Answers (domain-specific)
  // ============================================================
  if (context.preSparringAnswers) {
    const domainKey = mapDomainToPreSparringKey(domain);
    if (context.preSparringAnswers[domainKey]) {
      const preSparringActions = extractActionsFromText(
        context.preSparringAnswers[domainKey]
      );
      addUniqueActions(preSparringActions, domainActions, seenTitles, 'medium', `preSparring_${domain}`, domain, domainLabel);
    }
  }

  // ============================================================
  // BRON 6: Self-Test Knowledge Levels (domain-specific)
  // ============================================================
  if (context.selfTestResults?.knowledgeLevels) {
    const domainKey = mapDomainToKnowledgeKey(domain);
    const knowledgeLevel = context.selfTestResults.knowledgeLevels[domainKey];

    if (knowledgeLevel === 'new' || knowledgeLevel === 'learning') {
      const knowledgeActions = generateKnowledgeGapActions(domainLabel);
      addUniqueActions(knowledgeActions, domainActions, seenTitles, 'medium', `selfTest_knowledge_${domain}`, domain, domainLabel);
    }
  }

  // ============================================================
  // BRON 7: DISC/Workstyle (domain-adapted)
  // ============================================================
  if (context.completeAssessment) {
    const discActions = generateDISCActionsForDomain(
      context.completeAssessment.discProfile,
      domainLabel
    );
    addUniqueActions(discActions, domainActions, seenTitles, 'low', `disc_${domain}`, domain, domainLabel);

    const workstyleActions = generateWorkstyleActionsForDomain(
      context.completeAssessment.workstyleProfile,
      domainLabel
    );
    addUniqueActions(workstyleActions, domainActions, seenTitles, 'low', `workstyle_${domain}`, domain, domainLabel);
  }

  // ============================================================
  // FALLBACK: Domain-specific templates (if not enough actions)
  // ============================================================
  if (domainActions.length < 2) {
    const templateActions = generateDomainTemplateActions(domain, domainLabel);
    addUniqueActions(templateActions, domainActions, seenTitles, 'low', `template_${domain}`, domain, domainLabel);
  }

  return domainActions;
}

// =============================================================================
// HELPER FUNCTIONS: Add Unique Actions
// =============================================================================

/**
 * Add actions if not duplicate
 */
function addUniqueActions(
  actions: string[],
  allActions: GeneratedAction[],
  seenTitles: Set<string>,
  priority: ActionPriority,
  source: string,
  domain: CapabilityDomain,
  domainLabel: string
): void {
  for (const actionTitle of actions) {
    if (!isDuplicate(actionTitle, seenTitles)) {
      allActions.push({
        title: actionTitle,
        priority,
        actionType: detectActionTypeFromTitle(actionTitle),
        domain,
        domainLabel,
        source,
      });
      seenTitles.add(normalizeTitle(actionTitle));
    }
  }
}

/**
 * Normalize title for duplicate detection
 */
function normalizeTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/^(ik ga|ik wil|ik zou|we gaan|we willen|start|begin|maak|bouw|zet)\s+/i, '')
    .replace(/\s+/g, ' ')
    .replace(/[.,!?;:]/g, '')
    .trim();
}

/**
 * Check if action is duplicate (75% similarity threshold)
 */
function isDuplicate(
  action: string,
  seenTitles: Set<string>,
  similarityThreshold: number = 0.75
): boolean {
  const normalized = normalizeTitle(action);

  // Exact match
  if (seenTitles.has(normalized)) {
    return true;
  }

  // Similarity check
  for (const seen of Array.from(seenTitles)) {
    const similarity = calculateStringSimilarity(normalized, seen);
    if (similarity > similarityThreshold) {
      return true;
    }
  }

  return false;
}

/**
 * Calculate string similarity using Jaccard similarity
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  const words1 = new Set(str1.toLowerCase().split(/\s+/).filter(w => w.length > 2));
  const words2 = new Set(str2.toLowerCase().split(/\s+/).filter(w => w.length > 2));

  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set(Array.from(words1).filter(w => words2.has(w)));
  const union = new Set([...Array.from(words1), ...Array.from(words2)]);

  return intersection.size / union.size;
}

// =============================================================================
// HELPER FUNCTIONS: Extract Actions from Text
// =============================================================================

/**
 * Extract actions from text (split sentences, clean up)
 */
function extractActionsFromText(text: string): string[] {
  const actions: string[] = [];
  
  if (!text || text.length < 10) return actions;

  // Split into sentences
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 10);

  for (const sentence of sentences) {
    let cleaned = sentence.trim();
    
    // Remove common prefixes
    cleaned = cleaned.replace(/^(Ik ga|Ik wil|Ik zou|We gaan|We willen|Ik plan|Ik denk|Ik moet)\s+/i, '');
    
    // Capitalize first letter
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    
    if (cleaned.length > 10 && cleaned.length < 150) {
      actions.push(cleaned);
    }
  }

  return actions;
}

/**
 * Extract domain-specific actions from goal description
 */
function extractDomainSpecificActionsFromGoal(
  description: string,
  domain: CapabilityDomain
): string[] {
  const actions: string[] = [];
  if (!description || description.length < 20) return actions;

  const lowerDesc = description.toLowerCase();
  const domainKeywords = DOMAIN_KEYWORDS[domain];
  const sentences = description.split(/[.!?]+/).filter(s => s.trim().length > 10);

  for (const sentence of sentences) {
    const lowerSentence = sentence.toLowerCase();
    
    // Check if sentence is relevant to domain
    const isRelevant = domainKeywords.some(kw => lowerSentence.includes(kw.toLowerCase()));
    if (!isRelevant) continue;

    // Extract numbers → Actions with targets
    if (/\d+/.test(sentence)) {
      const trimmed = sentence.trim();
      if (trimmed.length > 15 && trimmed.length < 100) {
        let action = trimmed;
        if (!action.match(/^(Haal|Bereik|Krijg|Maak|Bouw|Lanceer|Start)/i)) {
          action = `Werk naar: ${action}`;
        }
        actions.push(action.charAt(0).toUpperCase() + action.slice(1));
      }
    }

    // Extract tools/platforms
    const tools = ['shopify', 'wordpress', 'instagram', 'facebook', 'linkedin', 'youtube', 'tiktok', 'google', 'mailchimp', 'canva', 'figma'];
    for (const tool of tools) {
      if (lowerSentence.includes(tool)) {
        actions.push(`Zet ${tool.charAt(0).toUpperCase() + tool.slice(1)} account/strategie op`);
        break;
      }
    }

    // Extract roles
    const roles = sentence.match(/\b(designer|developer|marketeer|consultant|coach|partner|investeerder|accountant|jurist)\b/gi);
    if (roles) {
      for (const role of roles) {
        actions.push(`Zoek geschikte ${role.toLowerCase()}`);
      }
    }
  }

  return actions.slice(0, 5); // Max 5 from goal description per domain
}

/**
 * Extract domain-specific actions from user response
 */
function extractDomainSpecificActionsFromResponse(
  response: string,
  domain: CapabilityDomain
): string[] {
  const actions: string[] = [];
  if (!response || response.length < 15) return actions;

  const domainKeywords = DOMAIN_KEYWORDS[domain];
  const lowerResponse = response.toLowerCase();
  
  // Check if response is relevant to domain
  const isRelevant = domainKeywords.some(kw => lowerResponse.includes(kw.toLowerCase()));
  if (!isRelevant && response.length < 100) return actions;

  // Extract intentions
  const intentMatch = response.match(/(Ik ga|Ik wil|Ik zou|We gaan|We willen|Ik plan)\s+(.+)/i);
  if (intentMatch && intentMatch[2]) {
    let action = intentMatch[2].trim();
    if (action.includes('.')) {
      action = action.split('.')[0];
    }
    if (action.length > 10 && action.length < 100) {
      action = action.replace(/^(ik|mijn|me|mezelf)\s+/i, '');
      actions.push(action.charAt(0).toUpperCase() + action.slice(1));
    }
  }

  // Extract names
  const names = response.match(/\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/g);
  if (names) {
    for (const name of names) {
      actions.push(`Neem contact op met ${name}`);
    }
  }

  // Extract resources
  const resources = response.match(/\b(cursus|training|workshop|masterclass|boek|artikel|video|podcast)\b/gi);
  if (resources) {
    for (const resource of resources) {
      const resourceName = extractResourceName(response, resource);
      actions.push(`Zoek ${resource.toLowerCase()}: ${resourceName}`);
    }
  }

  return actions.slice(0, 3); // Max 3 per response
}

/**
 * Extract resource name from text
 */
function extractResourceName(text: string, resource: string): string {
  const index = text.toLowerCase().indexOf(resource.toLowerCase());
  if (index === -1) return '';
  
  const afterResource = text.substring(index + resource.length).trim();
  const match = afterResource.match(/^['":]?\s*([^.'",]+)/);
  return match ? match[1].trim() : '';
}

// =============================================================================
// HELPER FUNCTIONS: Generate Specific Action Types
// =============================================================================

/**
 * Generate learning actions (adapted to learning style)
 */
function generateLearningActions(
  knowledge: string, 
  domainLabel: string, 
  learningStyle?: LearningStyle
): string[] {
  // Use learning style if available
  if (learningStyle) {
    return [
      generateLearningAction(knowledge, learningStyle),
      `Zoek een expert/mentor voor ${knowledge}`,
    ];
  }
  
  // Default: mixed approach
  return [
    `Volg een online cursus over ${knowledge}`,
    `Lees 3 artikelen over ${knowledge}`,
    `Zoek een expert/mentor voor ${knowledge}`,
    `Bekijk video tutorials over ${knowledge}`,
  ];
}

/**
 * Generate challenge-tackling actions
 */
function generateChallengeActions(challenge: string, domainLabel: string): string[] {
  const lowerChallenge = challenge.toLowerCase();
  const actions: string[] = [];

  if (lowerChallenge.includes('tijd')) {
    actions.push(`Plan 3 vaste tijdslots per week voor ${domainLabel}`);
    actions.push(`Blokkeer tijd in agenda voor ${domainLabel}`);
  } else if (lowerChallenge.includes('geld') || lowerChallenge.includes('budget')) {
    actions.push(`Vraag 3 offertes aan voor ${domainLabel}`);
    actions.push(`Bereken benodigd budget voor ${domainLabel}`);
  } else if (lowerChallenge.includes('kennis')) {
    actions.push(`Onderzoek beste aanpak voor: ${challenge.substring(0, 50)}`);
    actions.push(`Zoek 3 bronnen over: ${challenge.substring(0, 50)}`);
  } else {
    actions.push(`Maak actieplan om te tackelen: ${challenge.substring(0, 50)}`);
  }

  return actions;
}

/**
 * Generate integration actions
 */
function generateIntegrationActions(activity: string, domainLabel: string): string[] {
  return [
    `Integreer bestaande activiteit '${activity.substring(0, 40)}' in ${domainLabel}`,
    `Evalueer hoe '${activity.substring(0, 40)}' kan bijdragen aan ${domainLabel}`,
  ];
}

/**
 * Extract tools from text
 */
function extractToolsFromText(text: string): string[] {
  const tools: string[] = [];
  const lowerText = text.toLowerCase();
  
  const knownTools = ['shopify', 'wordpress', 'instagram', 'facebook', 'linkedin', 'youtube', 'tiktok', 'mailchimp', 'canva', 'figma', 'notion', 'trello'];
  
  for (const tool of knownTools) {
    if (lowerText.includes(tool)) {
      tools.push(tool);
    }
  }
  
  return tools;
}

/**
 * Generate tool setup actions
 */
function generateToolActions(tool: string, domainLabel: string): string[] {
  return [
    `Zet ${tool.charAt(0).toUpperCase() + tool.slice(1)} account op voor ${domainLabel}`,
    `Leer de basis van ${tool.charAt(0).toUpperCase() + tool.slice(1)} voor ${domainLabel}`,
  ];
}

/**
 * Generate skill development actions
 */
function generateSkillDevelopmentActions(skill: string, domainLabel: string): string[] {
  const skillName = skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  return [
    `Oefen ${skillName} met praktische oefeningen`,
    `Zoek een mentor voor ${skillName}`,
  ];
}

/**
 * Generate time planning actions
 */
function generateTimePlanningActions(timeInvestment: string, domainLabel: string): string[] {
  return [
    `Plan ${timeInvestment} per week voor ${domainLabel}`,
    `Blokkeer tijd in agenda: ${timeInvestment} voor ${domainLabel}`,
  ];
}

/**
 * Generate timeline actions
 */
function generateTimelineActions(timeline: string, domainLabel: string): string[] {
  return [
    `Bepaal exacte startdatum voor ${domainLabel}: ${timeline}`,
    `Plan eerste stappen voor ${domainLabel} in ${timeline}`,
  ];
}

/**
 * Generate budget actions
 */
function generateBudgetActions(budget: string, domainLabel: string): string[] {
  return [
    `Reserveer budget: ${budget} voor ${domainLabel}`,
    `Bereken kosten voor ${domainLabel}`,
  ];
}

/**
 * Generate learning actions for domain
 */
function generateLearningActionsForDomain(domainLabel: string): string[] {
  return [
    `Bouw basiskennis op over ${domainLabel}`,
    `Onderzoek beste aanpak voor ${domainLabel}`,
  ];
}

/**
 * Generate outsource actions
 */
function generateOutsourceActions(domainLabel: string): string[] {
  return [
    `Vraag 3 offertes aan voor ${domainLabel}`,
    `Zoek geschikte partner voor ${domainLabel}`,
  ];
}

/**
 * Generate hire actions
 */
function generateHireActions(domainLabel: string): string[] {
  return [
    `Schrijf vacature voor ${domainLabel}`,
    `Zoek geschikte kandidaat voor ${domainLabel}`,
  ];
}

/**
 * Generate knowledge gap actions
 */
function generateKnowledgeGapActions(domainLabel: string): string[] {
  return [
    `Bouw basiskennis op over ${domainLabel}`,
    `Volg introductiecursus over ${domainLabel}`,
  ];
}

/**
 * Generate DISC-based actions for domain
 */
function generateDISCActionsForDomain(
  discProfile: any,
  domainLabel: string
): string[] {
  const actions: string[] = [];
  
  if (discProfile?.primary === 'red' && discProfile.scores?.red >= 70) {
    actions.push(`Neem direct beslissing over aanpak ${domainLabel}`);
  }
  
  if (discProfile?.primary === 'yellow' && discProfile.scores?.yellow >= 70) {
    actions.push(`Netwerk met experts/partners voor ${domainLabel}`);
  }
  
  return actions;
}

/**
 * Generate workstyle-based actions for domain
 */
function generateWorkstyleActionsForDomain(
  workstyleProfile: any,
  domainLabel: string
): string[] {
  const actions: string[] = [];
  
  if (workstyleProfile?.scores?.quick_start >= 7) {
    actions.push(`Experimenteer met snelle test voor ${domainLabel}`);
  }
  
  if (workstyleProfile?.scores?.fact_finder >= 7) {
    actions.push(`Verzamel en analyseer data over ${domainLabel}`);
  }
  
  return actions;
}

/**
 * Generate domain-specific template actions
 */
function generateDomainTemplateActions(domain: CapabilityDomain, domainLabel: string): string[] {
  const templates: Record<CapabilityDomain, string[]> = {
    strategy: [
      `Bepaal strategie voor ${domainLabel}`,
      `Maak plan voor ${domainLabel}`,
    ],
    marketing: [
      `Ontwikkel marketing strategie voor ${domainLabel}`,
      `Plan marketing campagne voor ${domainLabel}`,
    ],
    sales: [
      `Bepaal sales aanpak voor ${domainLabel}`,
      `Plan sales proces voor ${domainLabel}`,
    ],
    product: [
      `Ontwikkel product voor ${domainLabel}`,
      `Plan product features voor ${domainLabel}`,
    ],
    operations: [
      `Zet operationele processen op voor ${domainLabel}`,
      `Automatiseer processen voor ${domainLabel}`,
    ],
    finance: [
      `Bereken budget voor ${domainLabel}`,
      `Plan financiële structuur voor ${domainLabel}`,
    ],
    team: [
      `Bepaal team behoeften voor ${domainLabel}`,
      `Plan team uitbreiding voor ${domainLabel}`,
    ],
  };
  
  return templates[domain] || [`Werk aan ${domainLabel}`];
}

// =============================================================================
// HELPER FUNCTIONS: Mapping
// =============================================================================

/**
 * Map domain to pre-sparring key
 */
function mapDomainToPreSparringKey(domain: CapabilityDomain): string {
  const mapping: Record<CapabilityDomain, string> = {
    strategy: 'structuur',
    marketing: 'marketing',
    sales: 'marketing',
    product: 'structuur',
    operations: 'structuur',
    finance: 'structuur',
    team: 'team',
  };
  return mapping[domain] || 'kennis';
}

/**
 * Map domain to knowledge level key
 */
function mapDomainToKnowledgeKey(domain: CapabilityDomain): string {
  const mapping: Record<CapabilityDomain, string> = {
    strategy: 'Strategie',
    marketing: 'Marketing',
    sales: 'Sales',
    product: 'Product',
    operations: 'Operatie',
    finance: 'Financiën',
    team: 'Team',
  };
  return mapping[domain];
}

// =============================================================================
// HELPER FUNCTIONS: Timeline Distribution
// =============================================================================

/**
 * Distribute actions over timeline
 */
function distributeActionsOverTimeline(
  actions: GeneratedAction[],
  startDate: Date,
  endDate: Date
): GeneratedAction[] {
  const durationMs = endDate.getTime() - startDate.getTime();
  if (durationMs <= 0) return actions;

  // Sort by priority
  const sorted = [...actions].sort((a, b) => {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  // Distribute evenly
  const distributed: GeneratedAction[] = [];
  const actionsPerWeek = Math.max(1, Math.ceil(sorted.length / (durationMs / (7 * 24 * 60 * 60 * 1000))));
  
  let currentDate = new Date(startDate);
  let actionIndex = 0;

  while (currentDate <= endDate && actionIndex < sorted.length) {
    const weekActions = sorted.slice(actionIndex, actionIndex + actionsPerWeek);
    
    for (let i = 0; i < weekActions.length; i++) {
      const offsetDays = Math.floor(i / Math.max(1, weekActions.length / 7));
      const actionDate = new Date(currentDate);
      actionDate.setDate(actionDate.getDate() + offsetDays);
      
      distributed.push({
        ...weekActions[i],
        // Note: targetDate would be added when saving to Firebase
      });
    }
    
    actionIndex += weekActions.length;
    currentDate.setDate(currentDate.getDate() + 7);
  }

  // Add remaining actions
  for (let i = actionIndex; i < sorted.length; i++) {
    distributed.push(sorted[i]);
  }

  return distributed;
}
